// share.ts — the M3 shareable result card ("beat my score on <TOPIC>").
//
// Renders a self-contained brag card to an offscreen <canvas> (pure 2D context — no Phaser, no
// network) with the score, topic, rule, and a short call-to-action, then ships it via the Web Share
// API where available (navigator.share with files), falling back to a copy-link + image-download.
//
// The DEEP LINK encodes ?topic=&seed= so a friend opening it lands on the same board to "beat my
// score". This is presentation only — it never touches Core/determinism (the seed is just echoed
// through to the URL the renderer already understands via registryKeys.parseBootParams).

import { PALETTE, hex, FONT } from './keys';
import { encodeGhost, ghostOutcomesFromCells } from './ghostRoute';
import { GHOST_BALANCE_VERSION } from './registryKeys';

/** Minimal per-cell shape we read to encode the spoiler-free route grid (subset of Core `Cell`). */
export interface RouteCell {
  kind: 'correct' | 'decoy' | 'wall';
  eaten: boolean;
  /** 0 = human, 1 = AI, null = nobody */
  eatenBy: number | null;
  /** a decoy that was routed THROUGH (free Gauntlet pass), not eaten */
  armed: boolean;
}

export interface ShareCardData {
  topicLabel: string;
  rule: string;
  score: number;
  /** human's best combo this run (optional flourish) */
  bestCombo?: number;
  /** correct/quota for an accuracy line (optional) */
  eatenCorrect?: number;
  quota?: number;
  won: boolean;
  /** seed + topic slug to build the "play this exact board" deep link */
  seed: number;
  topicNormalized: string;
  /** true if launched from Daily — adds a "Daily" ribbon */
  isDaily?: boolean;
  /** The daily twist the run was played under (-1/undefined = none). Minted into the link as &m=N so
   *  a cross-day open replays the SAME rules/layout the target + ghost were set on. */
  dailyMutatorId?: number;
  /** Wordle-style spoiler-free route grid inputs (the snapshot's final cells + dims). */
  cells?: RouteCell[];
  rows?: number;
  cols?: number;
  /** true if the human out-scored the rival this board (only meaningful in AI matches). */
  beatRival?: boolean;
  /** false in solo/practice (no rival) so the share text drops the "Beat the rival?" line. */
  hasRival?: boolean;
  /** P1.2b: the board index this snapshot is from. The recipient always replays board 0, so the
   *  per-cell ghost route is only layout-aligned (and thus emitted) when this is 0. */
  boardIndex?: number;
  /** P1.2c best-of-three: the series mode to encode (omit/'' = single challenge). */
  seriesMode?: string;
  /** P1.2c: the game number to encode into the friend's link. */
  seriesGame?: number;
  /** P1.2c: series wins as the RECIPIENT will read them ("you"). Caller swaps perspective. */
  seriesYou?: number;
  /** P1.2c: series wins as the RECIPIENT will read them ("them"). Caller swaps perspective. */
  seriesThem?: number;
  /** GHOST DUELS Slice B: the KV id of THIS run's uploaded ghost (opt-in). When present, the link carries
   *  &gr=<id> so the recipient fetches + races the actual replay. The existing &ghost + &bv stay exactly
   *  as-is (the fail-closed static-footprint fallback if the fetch/pin misses). '' / undefined = no &gr. */
  ghostRemoteId?: string;
  /** FACE & FURY S5: this run was a PERFECT GAUNTLET (won, zero decoys eaten, ≥3 corrects, every correct
   *  threaded). Adds the gold PERFECT GAUNTLET badge to the card + a matching brag line in the text. */
  perfectGauntlet?: boolean;
  /** S9 FIX-D: the REAL threaded count for this board (GameScene's gauntlet-event tally — the same
   *  number the Result's THREADED stat shows). The snapshot's armed cells can't carry it: arming
   *  expires 0.6s after a pass (sim.ts armedUntilMs), so counting amber cells at the final frame
   *  systematically under-reports (the "THREADED 2 vs 6 THREADED" contradiction). When present this
   *  drives the card legend + text stat line; the drawn-cell count remains the floor/fallback. */
  threads?: number;
}

// Spoiler-free route legend — encodes the OUTCOME (performance/path), never which answer was correct.
const RT_CORRECT_EATEN = '🟩'; // a correct cell the human EATEN (a good munch)
const RT_THREADED = '🟨'; // a decoy ROUTED THROUGH (free Gauntlet pass — "threaded it")
const RT_DECOY_EATEN = '🟥'; // a decoy the human EATEN (the mistake)
const RT_WALL = '⬛'; // wall
const RT_UNTOUCHED = '⬜'; // untouched cell — a correct and a decoy are INDISTINGUISHABLE here

/**
 * Build a compact, SPOILER-FREE emoji route grid (one string row per board row) from the final
 * snapshot cells. It encodes the player's OUTCOME/PATH, never the answer key:
 *   🟩 correct the human ate · 🟥 decoy the human ate · 🟨 decoy routed-through (free pass) ·
 *   ⬛ wall · ⬜ untouched.
 * Untouched correct and untouched decoy BOTH render as ⬜, so the grid never leaks which untouched
 * cell was the right answer. Green/red are attributed strictly to the human (`eatenBy === 0`), so an
 * AI rival's eats are never miscredited. Returns '' if dims/cells are missing or mismatched.
 */
export function buildRouteGrid(
  cells: RouteCell[] | undefined,
  rows: number | undefined,
  cols: number | undefined,
): string {
  if (!cells || !rows || !cols || rows <= 0 || cols <= 0) return '';
  if (cells.length !== rows * cols) return '';
  const out: string[] = [];
  for (let r = 0; r < rows; r += 1) {
    let line = '';
    for (let c = 0; c < cols; c += 1) {
      const cell = cells[r * cols + c];
      line += classifyRouteCell(cell);
    }
    out.push(line);
  }
  return out.join('\n');
}

/** Classify one cell to its spoiler-free emoji. Order matters: walls and human eats first. */
function classifyRouteCell(cell: RouteCell): string {
  if (cell.kind === 'wall') return RT_WALL;
  const humanAte = cell.eaten && cell.eatenBy === 0;
  if (humanAte) return cell.kind === 'correct' ? RT_CORRECT_EATEN : RT_DECOY_EATEN;
  // Routed-through-but-not-eaten decoy (the free Gauntlet pass). Only decoys ever arm.
  if (cell.armed && cell.kind === 'decoy') return RT_THREADED;
  // Untouched (incl. an un-armed decoy or an uneaten correct) — kept indistinguishable on purpose.
  return RT_UNTOUCHED;
}

// #66 — the CANVAS route render is PATH-DRAWN, not emoji. The shared emoji legend (above) stays the
// TEXT-share substrate (universal in a plain-text/clipboard block), but the poster cards render each
// cell as a rounded rect tinted by its OUTCOME state so the hero art is device-independent (no
// Segoe/Apple/Noto emoji drift) and matches the drawn ★ badge. Same spoiler-free semantics as the
// emoji legend — untouched correct and untouched decoy are indistinguishable.
type RouteState = 'correct' | 'threaded' | 'decoy' | 'wall' | 'untouched';

/** State classifier mirroring classifyRouteCell, but for the path-drawn canvas grid (state, not glyph). */
function classifyRouteState(cell: RouteCell): RouteState {
  if (cell.kind === 'wall') return 'wall';
  const humanAte = cell.eaten && cell.eatenBy === 0;
  if (humanAte) return cell.kind === 'correct' ? 'correct' : 'decoy';
  if (cell.armed && cell.kind === 'decoy') return 'threaded';
  return 'untouched';
}

/** Per-state fill for the drawn route cells (locked palette: correct/armed/hazard/wall/tile). */
const ROUTE_FILL: Record<RouteState, string> = {
  correct: hex(PALETTE.correct), // a correct the human ate (a good munch)
  threaded: hex(PALETTE.armed), // a decoy routed THROUGH (free Gauntlet pass — "threaded it")
  decoy: hex(PALETTE.hazard), // a decoy the human ate (the mistake)
  wall: hex(PALETTE.wall), // wall (a hole in the field)
  untouched: hex(PALETTE.tile), // untouched — a correct and a decoy are indistinguishable here
};

/** Build the 2D outcome-state grid the canvas render draws (parallels buildRouteGrid's emoji rows). */
function buildRouteStates(
  cells: RouteCell[] | undefined,
  rows: number | undefined,
  cols: number | undefined,
): RouteState[][] | null {
  if (!cells || !rows || !cols || rows <= 0 || cols <= 0) return null;
  if (cells.length !== rows * cols) return null;
  const out: RouteState[][] = [];
  for (let r = 0; r < rows; r += 1) {
    const line: RouteState[] = [];
    for (let c = 0; c < cols; c += 1) line.push(classifyRouteState(cells[r * cols + c]));
    out.push(line);
  }
  return out;
}

/** Count the route's signature moments from the spoiler-free grid (leaks nothing the grid doesn't).
 *  S9 FIX-D: `realThreads` (the board's gauntlet-event tally) outranks the grid's 🟨 count when larger —
 *  arming expires 0.6s after a pass, so the final-frame grid under-reports threads (root cause of the
 *  legend/stat contradiction). The 🟨 count stays the FLOOR so the line never claims fewer threads than
 *  the grid visibly shows. */
function routeStatsLine(grid: string, realThreads?: number): string {
  if (!grid) return '';
  const shown = (grid.match(/🟨/gu) ?? []).length; // decoys ROUTED THROUGH still armed at the final frame
  const threaded = Math.max(shown, Math.floor(realThreads ?? 0));
  const slipped = (grid.match(/🟥/gu) ?? []).length; // decoys EATEN (the mistakes)
  const parts: string[] = [];
  if (threaded) parts.push(`🧵 threaded ${threaded}`);
  if (slipped) parts.push(`⚠ slipped ${slipped}`);
  return parts.join('  ');
}

/**
 * Build the concise, shareable, Wordle-style text body: a "my route" header, the spoiler-free emoji
 * route grid, a threaded/slipped stat line, then a route-framed "beat my route" verdict + the deep
 * link. Real newlines (not <br>). The grid/stats are omitted gracefully when cells/dims are missing.
 */
export function buildShareText(data: ShareCardData, link: string): string {
  const lines: string[] = [];
  lines.push(`Trivia Munchers — my route · ${data.topicLabel} ${data.score}pts`);
  // FACE & FURY S5: the signature clip line — every correct threaded, zero decoys eaten (the peak brag).
  if (data.perfectGauntlet) lines.push('✨ PERFECT GAUNTLET — every answer threaded, zero slips');
  const grid = buildRouteGrid(data.cells, data.rows, data.cols);
  if (grid) {
    lines.push(grid);
    const stats = routeStatsLine(grid, data.threads);
    if (stats) lines.push(stats);
  }
  if (data.hasRival) {
    lines.push(`Beat my route? (I ${data.beatRival ? 'out-ran' : 'lost to'} the rival)  ${link}`);
  } else {
    lines.push(`Beat my route on ${data.topicLabel}  ${link}`);
  }
  return lines.join('\n');
}

/** Build the deep link a friend taps to play the same board and try to beat the score. */
export function buildDeepLink(data: ShareCardData): string {
  let origin = '';
  let path = '/';
  try {
    origin = window.location.origin;
    path = window.location.pathname || '/';
  } catch {
    /* non-browser — return a relative link */
  }
  const params = new URLSearchParams();
  params.set('topic', data.topicNormalized);
  params.set('seed', String(data.seed >>> 0));
  params.set('play', '1'); // jump straight into the board
  if (data.isDaily) params.set('daily', '1');
  // Daily twist stamp: without it a link opened after UTC midnight would rebuild the board under a
  // DIFFERENT day's mutator — misaligned ghost markers + a score target set under different rules.
  if (data.isDaily && data.dailyMutatorId !== undefined && data.dailyMutatorId >= 0) {
    params.set('m', String(data.dailyMutatorId));
  }
  // P1.2 challenge: carry the sharer's score so the friend opens the SAME board with a target to beat.
  if (data.score > 0) params.set('target', String(Math.floor(data.score)));
  // P1.2b: carry the sharer's ROUTE so the friend races a ghost of their path/mistakes — but ONLY for
  // a board-0 share. The recipient always replays board 0 (same seed^imul(1,…) + difficulty(0) +
  // topic), so a route encoded on board N>0 (different seed/layout) would paint markers on the WRONG
  // cells and mislabel kinds — actively misleading. Board-0 shares align exactly; others drop the ghost.
  const ghost = (data.boardIndex ?? 0) === 0 ? encodeGhost(ghostOutcomesFromCells(data.cells)) : '';
  if (ghost) {
    params.set('ghost', ghost);
    // Balance-version stamp: the decoder drops ghosts minted against a different board-layout
    // generation (fence-floor tune 2026-07-01) instead of painting markers on wrong cells.
    params.set('bv', GHOST_BALANCE_VERSION);
  }
  // GHOST DUELS Slice B: if THIS run was uploaded as a raceable ghost, add &gr=<id> so the recipient
  // fetches + races the live spectral replay. Purely additive — &ghost + &bv above remain the fallback,
  // so a cold/missing/bv-mismatched blob degrades to exactly today's static-footprint behavior.
  const gr = (data.ghostRemoteId ?? '').trim();
  if (gr && /^[A-Za-z0-9-]{1,64}$/.test(gr)) params.set('gr', gr);
  // P1.2c best-of-three: carry the series standings (already perspective-swapped by the caller) so the
  // friend's link continues the series. &series=bo3 &g=<game> &y=<recipient wins> &t=<sender wins>.
  if (data.seriesMode) {
    params.set('series', data.seriesMode);
    params.set('g', String(Math.max(1, Math.floor(data.seriesGame ?? 1))));
    params.set('y', String(Math.max(0, Math.floor(data.seriesYou ?? 0))));
    params.set('t', String(Math.max(0, Math.floor(data.seriesThem ?? 0))));
  }
  const qs = params.toString();
  return `${origin}${path}?${qs}`;
}

const CARD_W = 1080;
const CARD_H = 1080; // square — the fallback card / file for targets that reject the portrait
const CARD_PW = 1080;
const CARD_PH = 1920; // 9:16 portrait — the route-replay share artifact (vertical-native social)

/** Render the brag card to a canvas. Returns the canvas (caller can toBlob/toDataURL). */
export function renderShareCard(data: ShareCardData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // background — OLED dark with a subtle vertical gradient toward the brand accent.
  const grad = ctx.createLinearGradient(0, 0, 0, CARD_H);
  grad.addColorStop(0, hex(PALETTE.bg));
  grad.addColorStop(1, '#0a0c14');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // accent frame — PERFECT GAUNTLET forces the gold frame (it's always a win) as its first badge cue.
  ctx.strokeStyle = hex(data.won || data.perfectGauntlet ? PALETTE.gold : PALETTE.player);
  ctx.lineWidth = data.perfectGauntlet ? 14 : 10;
  ctx.strokeRect(28, 28, CARD_W - 56, CARD_H - 56);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // brand wordmark — measured adjacent lockup (fixed offsets collided under wide font fallbacks:
  // the shipped cards read "TRIVIAMUNCHERS" mashed together on 100% of exports).
  drawWordmark(ctx, CARD_W / 2, 150, 64);

  // optional Daily ribbon
  if (data.isDaily) {
    ctx.fillStyle = hex(PALETTE.armed);
    ctx.font = `bold 34px ${FONT.family}`;
    ctx.fillText('★ DAILY CHALLENGE ★', CARD_W / 2, 220);
  }

  // FACE & FURY S5 — PERFECT GAUNTLET hero badge (sits just under the daily ribbon slot).
  // Drawn gold stars instead of the ✨ emoji: canvas emoji rendering is platform-dependent
  // (flat mono star or tofu on locked-down font stacks); a path-drawn star is deterministic.
  if (data.perfectGauntlet) {
    drawStarBadge(ctx, 'PERFECT GAUNTLET', CARD_W / 2, data.isDaily ? 268 : 236, 40);
  }

  // headline — "I scored …"
  ctx.fillStyle = hex(PALETTE.textDim);
  ctx.font = `40px ${FONT.family}`;
  ctx.fillText(data.won ? 'My winning route on' : 'My route on', CARD_W / 2, 330);

  // topic (wrapped, large)
  ctx.fillStyle = hex(PALETTE.player);
  ctx.font = `bold 76px ${FONT.family}`;
  wrapText(ctx, data.topicLabel.toUpperCase(), CARD_W / 2, 430, CARD_W - 180, 86, 2);

  // big score — numeric hero = display face (two-face contract)
  ctx.fillStyle = hex(data.won ? PALETTE.gold : PALETTE.text);
  ctx.font = `bold 200px ${FONT.display}`;
  ctx.fillText(String(data.score), CARD_W / 2, 660);
  ctx.fillStyle = hex(PALETTE.textDim);
  ctx.font = `36px ${FONT.family}`;
  ctx.fillText('POINTS', CARD_W / 2, 770);

  // stat chips (combo / accuracy) — DRAWN icons (flame + bullseye), not 🔥/🎯 emoji (#66).
  drawStatChips(ctx, CARD_W / 2, 850, 40, data);

  // call to action — the PERFECT GAUNTLET run brags the feat right on the CTA.
  ctx.fillStyle = hex(data.perfectGauntlet ? PALETTE.gold : PALETTE.armed);
  ctx.font = `bold ${data.perfectGauntlet ? 40 : 48}px ${FONT.family}`;
  ctx.fillText(data.perfectGauntlet ? 'CAN YOU GO PERFECT?' : 'BEAT MY ROUTE', CARD_W / 2, 940);

  // No route grid on the SQUARE card: at CARD_H=1080 the grid started at y=1010 and clipped to
  // ~1 of 6 rows against the 1052 frame edge — a half-drawn artifact worse than none (9+ punch #2).
  // The route lives where it fits: the share TEXT emoji grid + the portrait card's hero band.

  return canvas;
}

/**
 * Render the 9:16 PORTRAIT "route replay" card — the vertical-native share artifact. Same spoiler-safe
 * data as the square card (snapshot route grid, never untouched-cell correctness), reframed tall with the
 * ROUTE as the hero element + the threaded/slipped stat line. Built entirely on-device (canvas) — no deps,
 * no network. (Snapshot-only beats: the animated webm tier is deferred behind an explicit video affordance.)
 */
export function renderRouteCardPortrait(data: ShareCardData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_PW;
  canvas.height = CARD_PH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const grad = ctx.createLinearGradient(0, 0, 0, CARD_PH);
  grad.addColorStop(0, hex(PALETTE.bg));
  grad.addColorStop(1, '#0a0c14');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_PW, CARD_PH);

  ctx.strokeStyle = hex(data.won || data.perfectGauntlet ? PALETTE.gold : PALETTE.player);
  ctx.lineWidth = data.perfectGauntlet ? 16 : 12;
  ctx.strokeRect(32, 32, CARD_PW - 64, CARD_PH - 64);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // brand wordmark — measured adjacent lockup (see renderShareCard note; same collision fix).
  drawWordmark(ctx, CARD_PW / 2, 150, 66);

  if (data.isDaily) {
    ctx.fillStyle = hex(PALETTE.armed);
    ctx.font = `bold 36px ${FONT.family}`;
    ctx.fillText("★ TODAY'S GAUNTLET ★", CARD_PW / 2, 250);
  }

  // FACE & FURY S5 — PERFECT GAUNTLET hero badge on the vertical-native card.
  if (data.perfectGauntlet) {
    ctx.fillStyle = hex(PALETTE.gold);
    drawStarBadge(ctx, 'PERFECT GAUNTLET', CARD_PW / 2, data.isDaily ? 300 : 262, 46);
  }

  // headline + topic
  ctx.fillStyle = hex(PALETTE.textDim);
  ctx.font = `42px ${FONT.family}`;
  ctx.fillText(data.won ? 'MY WINNING ROUTE ON' : 'MY ROUTE ON', CARD_PW / 2, 360);
  ctx.fillStyle = hex(PALETTE.player);
  ctx.font = `bold 82px ${FONT.family}`;
  wrapText(ctx, data.topicLabel.toUpperCase(), CARD_PW / 2, 480, CARD_PW - 200, 92, 2);

  // the ROUTE is the hero of the portrait — PATH-DRAWN cells tinted by outcome (#66), so the poster is
  // device-independent (no platform-emoji drift) and matches the drawn ★ badge. Centred in a tall band.
  const states = buildRouteStates(data.cells, data.rows, data.cols);
  if (states) {
    const bandTop = 620;
    const bandH = 620;
    const gridBottom = drawRouteGridCanvas(ctx, states, CARD_PW / 2, bandTop, CARD_PW - 220, bandH);
    let correctShown = 0;
    let threadedShown = 0;
    let slipped = 0;
    for (const row of states) for (const s of row) {
      if (s === 'correct') correctShown += 1;
      else if (s === 'threaded') threadedShown += 1;
      else if (s === 'decoy') slipped += 1;
    }
    // Drawn swatch legend (amber = threaded, red = slipped) — doubles as the grid key.
    // S9 FIX-D (legend honesty): the drawn amber cells only cover decoys STILL armed at the final frame
    // (arming expires 0.6s after a pass — sim.ts), so a perfect run's card read "THREADED 2" against the
    // run's "6 THREADED" stat. The REAL gauntlet-event tally (data.threads) drives the number when the
    // caller supplies it; the drawn-cell count stays the FLOOR so the key never claims fewer threads
    // than the grid visibly shows.
    const threaded = Math.max(threadedShown, Math.floor(data.threads ?? 0));
    drawRouteStatRow(ctx, CARD_PW / 2, gridBottom + 54, correctShown, threaded, slipped, 52);
  }

  // big score + units — numeric hero = display face (two-face contract)
  ctx.fillStyle = hex(data.won ? PALETTE.gold : PALETTE.text);
  ctx.font = `bold 196px ${FONT.display}`;
  ctx.fillText(String(data.score), CARD_PW / 2, 1530);
  ctx.fillStyle = hex(PALETTE.textDim);
  ctx.font = `38px ${FONT.family}`;
  ctx.fillText('POINTS', CARD_PW / 2, 1642);

  // combo / accuracy chips — DRAWN icons (flame + bullseye), not 🔥/🎯 emoji (#66).
  drawStatChips(ctx, CARD_PW / 2, 1728, 42, data);

  // call to action — brag the PERFECT GAUNTLET feat right on the CTA.
  ctx.fillStyle = hex(data.perfectGauntlet ? PALETTE.gold : PALETTE.armed);
  ctx.font = `bold ${data.perfectGauntlet ? 50 : 58}px ${FONT.family}`;
  ctx.fillText(data.perfectGauntlet ? 'CAN YOU GO PERFECT?' : 'BEAT MY ROUTE', CARD_PW / 2, 1828);

  return canvas;
}

/** The shareable replay artifact. Today always an image (9:16 portrait card); the video kind is reserved. */
export type ReplayArtifact =
  | { kind: 'image'; blob: Blob; w: number; h: number }
  | { kind: 'video'; blob: Blob; mime: string; w: number; h: number };

/**
 * Produce the first-class share artifact: a static 9:16 portrait route card (instant, universal,
 * spoiler-safe, zero-dep). The animated webm tier is intentionally deferred — a Capacitor WebView
 * MediaRecorder muxes webm-only, which several social targets reject, so it would ship behind an
 * explicit "Share as video" affordance + a capability gate. Returns null only if the canvas can't blob.
 */
export async function renderRouteReplay(data: ShareCardData): Promise<ReplayArtifact | null> {
  const blob = await canvasToBlob(renderRouteCardPortrait(data));
  return blob ? { kind: 'image', blob, w: CARD_PW, h: CARD_PH } : null;
}

/**
 * Two-tone TRIVIA MUNCHERS lockup, centered on cx, laid ADJACENTLY from measured widths — never
 * fixed offsets (glyph widths vary by platform font fallback, which is how the shipped cards
 * collided into "TRIVIAMUNCHERS").
 */
function drawWordmark(ctx: CanvasRenderingContext2D, cx: number, y: number, sizePx: number): void {
  // Brand lockup = DISPLAY face per the S8 two-face contract (content face carries body/topic text).
  ctx.font = `bold ${sizePx}px ${FONT.display}`;
  // #12 fallback guard: measure + paint share THIS one synchronous tick, so a font swap can't land
  // between them — but if measureText under-reports on a system-ui/Fredoka fallback (or a headless
  // context with degenerate metrics), the two words could still merge into "TRMIA…". Floor each word to
  // a conservative per-glyph estimate (~0.5em, always ≤ the real bold-cap advance so a correct measure
  // wins the Math.max) and floor the gap. A too-wide floor only pads the lockup — it can never overlap.
  const estWidth = (s: string): number => Math.max(ctx.measureText(s).width, s.length * sizePx * 0.5);
  const gap = Math.max(Math.round(sizePx * 0.42), Math.round(sizePx * 0.28));
  const wT = estWidth('TRIVIA');
  const wM = estWidth('MUNCHERS');
  const left = cx - (wT + gap + wM) / 2;
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  ctx.fillStyle = hex(PALETTE.text);
  ctx.fillText('TRIVIA', left, y);
  ctx.fillStyle = hex(PALETTE.correct);
  ctx.fillText('MUNCHERS', left + wT + gap, y);
  ctx.textAlign = prevAlign;
}

/** Path-drawn five-point star (deterministic across platforms, unlike canvas emoji). */
function drawStar(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.45;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const px = cx + Math.cos(a) * rad;
    const py = cy + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
}

/** Gold badge line flanked by two drawn stars: "★ TEXT ★" without emoji-font dependence. */
function drawStarBadge(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  y: number,
  sizePx: number,
): void {
  ctx.fillStyle = hex(PALETTE.gold);
  ctx.font = `bold ${sizePx}px ${FONT.display}`;
  ctx.fillText(text, cx, y);
  const half = ctx.measureText(text).width / 2;
  const r = sizePx * 0.42;
  drawStar(ctx, cx - half - r - Math.round(sizePx * 0.45), y, r);
  drawStar(ctx, cx + half + r + Math.round(sizePx * 0.45), y, r);
}

// ── #66 path-drawn poster helpers (deterministic, emoji-free) ────────────────────────────────────

/** Trace a rounded-rect path (no fill/stroke) — deterministic across platforms, unlike ctx.roundRect. */
function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Draw the spoiler-free route grid as PATH-DRAWN rounded cells tinted by outcome state (replaces the
 * platform-emoji grid). Acted cells (correct/threaded/decoy) get a top sheen for poster material depth;
 * untouched/wall stay quiet with a hairline. Centred in the (cx, bandTop..bandTop+bandH, bandW) band.
 * Returns the grid's bottom Y so the caller can seat the legend under it.
 */
function drawRouteGridCanvas(
  ctx: CanvasRenderingContext2D,
  states: RouteState[][],
  cx: number,
  bandTop: number,
  bandW: number,
  bandH: number,
): number {
  const rows = states.length;
  const cols = states[0]?.length ?? 0;
  if (rows <= 0 || cols <= 0) return bandTop;
  const gap = Math.max(4, Math.round(bandW * 0.012));
  const cell = Math.min(
    Math.floor((bandW - gap * (cols - 1)) / cols),
    Math.floor((bandH - gap * (rows - 1)) / rows),
    120,
  );
  const gridW = cols * cell + (cols - 1) * gap;
  const gridH = rows * cell + (rows - 1) * gap;
  const startX = cx - gridW / 2;
  const startY = bandTop + (bandH - gridH) / 2;
  const r = Math.max(4, Math.round(cell * 0.22));
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const st = states[row][col];
      const x = startX + col * (cell + gap);
      const y = startY + row * (cell + gap);
      const acted = st === 'correct' || st === 'threaded' || st === 'decoy';
      roundRectPath(ctx, x, y, cell, cell, r);
      ctx.fillStyle = ROUTE_FILL[st];
      ctx.globalAlpha = st === 'wall' ? 0.85 : 1;
      ctx.fill();
      ctx.globalAlpha = 1;
      if (acted) {
        // Inner top highlight — a glossy tile read (poster depth), clipped to the cell.
        ctx.save();
        roundRectPath(ctx, x, y, cell, cell, r);
        ctx.clip();
        ctx.fillStyle = 'rgba(255,255,255,0.16)';
        ctx.fillRect(x, y, cell, cell * 0.44);
        ctx.restore();
      }
      roundRectPath(ctx, x + 1, y + 1, cell - 2, cell - 2, Math.max(3, r - 1));
      ctx.lineWidth = 2;
      ctx.strokeStyle = st === 'untouched' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.30)';
      ctx.stroke();
    }
  }
  return startY + gridH;
}

/** A drawn flame (combo icon) — a rounded teardrop, no emoji-font dependence. */
function drawFlame(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, color: string): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, cy - s * 0.55);
  ctx.bezierCurveTo(cx + s * 0.55, cy - s * 0.12, cx + s * 0.36, cy + s * 0.55, cx, cy + s * 0.55);
  ctx.bezierCurveTo(cx - s * 0.36, cy + s * 0.55, cx - s * 0.55, cy - s * 0.16, cx - s * 0.06, cy - s * 0.32);
  ctx.bezierCurveTo(cx + s * 0.02, cy - s * 0.18, cx + s * 0.12, cy - s * 0.34, cx, cy - s * 0.55);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** A drawn bullseye (accuracy icon) — concentric rings + centre dot, no emoji-font dependence. */
function drawTarget(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(2, s * 0.12);
  ctx.beginPath();
  ctx.arc(cx, cy, s * 0.48, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, s * 0.28, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, s * 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Draw the combo/accuracy chip row with DRAWN icons (flame + bullseye) instead of 🔥/🎯 emoji. */
function drawStatChips(
  ctx: CanvasRenderingContext2D,
  cx: number,
  y: number,
  fontPx: number,
  data: ShareCardData,
): void {
  const entries: Array<{ kind: 'flame' | 'target'; text: string; color: string }> = [];
  if (data.bestCombo && data.bestCombo >= 2) {
    entries.push({ kind: 'flame', text: `${data.bestCombo} COMBO`, color: hex(PALETTE.armed) });
  }
  if (typeof data.eatenCorrect === 'number' && typeof data.quota === 'number' && data.quota > 0) {
    entries.push({ kind: 'target', text: `${data.eatenCorrect}/${data.quota}`, color: hex(PALETTE.correct) });
  }
  if (!entries.length) return;
  ctx.font = `bold ${fontPx}px ${FONT.family}`;
  const iconS = fontPx * 1.0;
  const iconGap = fontPx * 0.32;
  const chipGap = fontPx * 1.1;
  const measured = entries.map((e) => ({ ...e, w: iconS + iconGap + ctx.measureText(e.text).width }));
  const total = measured.reduce((a, m) => a + m.w, 0) + chipGap * (measured.length - 1);
  const prevAlign = ctx.textAlign;
  const prevBase = ctx.textBaseline;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let x = cx - total / 2;
  for (const m of measured) {
    if (m.kind === 'flame') drawFlame(ctx, x + iconS / 2, y, iconS, m.color);
    else drawTarget(ctx, x + iconS / 2, y, iconS, m.color);
    ctx.fillStyle = hex(PALETTE.text);
    ctx.fillText(m.text, x + iconS + iconGap, y);
    x += m.w + chipGap;
  }
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBase;
}

/** Draw the route key as colour-swatch chips matching the grid (no emoji). */
function drawRouteStatRow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  y: number,
  eaten: number,
  threaded: number,
  slipped: number,
  fontPx: number,
): void {
  const entries: Array<{ color: string; label: string }> = [];
  if (eaten > 0) entries.push({ color: hex(PALETTE.correct), label: `EATEN ${eaten}` });
  if (threaded > 0) entries.push({ color: hex(PALETTE.armed), label: `SAFE THREADS ${threaded}` });
  if (slipped > 0) entries.push({ color: hex(PALETTE.hazard), label: `SLIPPED ${slipped}` });
  if (!entries.length) return;
  ctx.font = `bold ${fontPx}px ${FONT.family}`;
  const sw = fontPx * 0.92;
  const swGap = fontPx * 0.4;
  const chipGap = fontPx * 1.0;
  const measured = entries.map((e) => ({ ...e, w: sw + swGap + ctx.measureText(e.label).width }));
  const total = measured.reduce((a, m) => a + m.w, 0) + chipGap * (measured.length - 1);
  const prevAlign = ctx.textAlign;
  const prevBase = ctx.textBaseline;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let x = cx - total / 2;
  for (const m of measured) {
    roundRectPath(ctx, x, y - sw / 2, sw, sw, sw * 0.28);
    ctx.fillStyle = m.color;
    ctx.fill();
    ctx.fillStyle = hex(PALETTE.text);
    ctx.fillText(m.label, x + sw + swGap, y);
    x += m.w + chipGap;
  }
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBase;
}

/** Minimal word-wrap for the 2D context, centered, capped at maxLines. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  startY: number,
  maxWidth: number,
  lineH: number,
  maxLines: number,
): void {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const wrapped: string[] = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      wrapped.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) wrapped.push(line);

  const lines = wrapped.length > maxLines ? wrapped.slice(0, maxLines) : wrapped;
  // if we truncated, ellipsize the last visible line
  if (wrapped.length > maxLines && lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 1) last = last.slice(0, -1);
    lines[maxLines - 1] = `${last}…`;
  }
  const totalH = (lines.length - 1) * lineH;
  let y = startY - totalH / 2;
  for (const l of lines) {
    ctx.fillText(l, cx, y);
    y += lineH;
  }
}

/** Promise wrapper around canvas.toBlob. */
function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png');
    } catch {
      resolve(null);
    }
  });
}

export type ShareOutcome =
  | { method: 'shared' }
  | { method: 'copied'; link: string }
  | { method: 'downloaded'; link: string }
  | { method: 'failed'; link: string };

/**
 * Share the result. Order of preference:
 *   1) navigator.share with the card image + deep link (mobile native share sheet),
 *   2) navigator.share with text + link only (no file support),
 *   3) copy the deep link to the clipboard,
 *   4) trigger an image download + return the link to show inline.
 * Returns what actually happened so the UI can confirm ("Copied link!", "Shared!", …).
 */
export async function shareResult(data: ShareCardData): Promise<ShareOutcome> {
  const link = buildDeepLink(data);
  const title = 'Trivia Munchers';
  const text = buildShareText(data, link);

  // Prefer the 9:16 portrait route card (vertical-native social); keep the square card as the fallback
  // FILE for targets that reject the portrait, then degrade to link → clipboard → download.
  const replay = await renderRouteReplay(data);
  const squareBlob = await canvasToBlob(renderShareCard(data));
  const replayFile = replay ? new File([replay.blob], 'trivia-munchers-route.png', { type: 'image/png' }) : null;
  const squareFile = squareBlob ? new File([squareBlob], 'trivia-munchers-score.png', { type: 'image/png' }) : null;

  try {
    const nav = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean;
      share?: (d: ShareData) => Promise<void>;
    };
    if (typeof nav.share === 'function') {
      // 1) native share with a file — portrait first, square as the canShare fallback.
      for (const file of [replayFile, squareFile]) {
        if (!file) continue;
        const shareData: ShareData = { title, text, url: link, files: [file] } as ShareData;
        if (!nav.canShare || nav.canShare(shareData)) {
          await nav.share(shareData);
          return { method: 'shared' };
        }
      }
      // 2) native share, link only
      await nav.share({ title, text, url: link });
      return { method: 'shared' };
    }
  } catch (err) {
    // user cancelled the share sheet — treat as a soft no-op (don't fall through to download).
    if (err instanceof DOMException && err.name === 'AbortError') return { method: 'failed', link };
    // otherwise fall through to clipboard/download
  }

  // 3) clipboard — `text` already embeds the deep link (the Wordle-style block), so write it as-is.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return { method: 'copied', link };
    }
  } catch {
    /* clipboard blocked — fall through */
  }

  // 4) download the card image so the player still has something to post (portrait, else square).
  try {
    const dlBlob = replay?.blob ?? squareBlob;
    if (dlBlob) {
      const url = URL.createObjectURL(dlBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = replay ? 'trivia-munchers-route.png' : 'trivia-munchers-score.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      return { method: 'downloaded', link };
    }
  } catch {
    /* nothing more we can do */
  }
  return { method: 'failed', link };
}
